import * as React from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@/lib/utils";
import { Check, CaretUpDown } from "@phosphor-icons/react";

/**
 * SearchableSelect - Universal searchable dropdown
 * 
 * Props:
 * - options: [{ value, label, sublabel?, group? }]
 * - value: selected value
 * - onValueChange: (value) => void
 * - placeholder: string
 * - searchPlaceholder: string
 * - emptyText: string
 * - className: string
 * - disabled: boolean
 * - data-testid: string
 */
export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found",
  className,
  disabled = false,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);

  const selected = options.find((o) => o.value === value);

  // Group options if any have a group field
  const hasGroups = options.some((o) => o.group);
  const grouped = React.useMemo(() => {
    if (!hasGroups) return null;
    const map = {};
    options.forEach((o) => {
      const g = o.group || "Other";
      if (!map[g]) map[g] = [];
      map[g].push(o);
    });
    return map;
  }, [options, hasGroups]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          data-testid={testId}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          <CaretUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {hasGroups && grouped
              ? Object.entries(grouped).map(([group, items]) => (
                  <CommandGroup key={group} heading={group}>
                    {items.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label + (option.sublabel || "")}
                        onSelect={() => {
                          onValueChange(option.value === value ? "" : option.value);
                          setOpen(false);
                        }}
                        data-testid={`option-${option.value}`}
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="truncate">{option.label}</span>
                          {option.sublabel && (
                            <span className="text-xs text-muted-foreground truncate">{option.sublabel}</span>
                          )}
                        </div>
                        {value === option.value && (
                          <Check size={14} className="shrink-0 text-[#002FA7]" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))
              : (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label + (option.sublabel || "")}
                      onSelect={() => {
                        onValueChange(option.value === value ? "" : option.value);
                        setOpen(false);
                      }}
                      data-testid={`option-${option.value}`}
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate">{option.label}</span>
                        {option.sublabel && (
                          <span className="text-xs text-muted-foreground truncate">{option.sublabel}</span>
                        )}
                      </div>
                      {value === option.value && (
                        <Check size={14} className="shrink-0 text-[#002FA7]" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
